import { calculateItemTotalCost, type ItineraryCostItem } from "./itineraryCostUtils";

/**
 * Pricing treatment for itinerary items.
 * - included: part of the base total (default, all legacy items)
 * - optional: shown as an optional extra, cost NOT in the base total
 * - alternative: alternative to another item (e.g. hotel B vs hotel A), cost NOT in base total
 */
export type PricingTreatment = "included" | "optional" | "alternative";

export interface ChoiceItem extends ItineraryCostItem {
  id: string;
  title?: string | null;
  custom_title?: string | null;
  pricing_treatment?: string | null;
  alternative_to_item_id?: string | null;
  /** "Goes with": this option is chosen automatically when the linked option (in another choice) is chosen. */
  linked_option_item_id?: string | null;
  client_price?: number | null;
}

export interface QuoteClientSelections {
  optional_item_ids: string[];
  /** primary item id -> chosen item id (primary itself or an alternative) */
  alternative_selections: Record<string, string>;
  adjusted_total?: number;
  /** Client-chosen headcount (>= quoted pax); total scales linearly per person. */
  selected_pax?: number;
}

export const EMPTY_SELECTIONS: QuoteClientSelections = { optional_item_ids: [], alternative_selections: {} };

export function pricingTreatment(item: { pricing_treatment?: string | null }): PricingTreatment {
  const t = item?.pricing_treatment;
  return t === "optional" || t === "alternative" ? t : "included";
}

export function isBasePriced(item: { pricing_treatment?: string | null }): boolean {
  return pricingTreatment(item) === "included";
}

/** Item cost that counts towards the base quote total (0 for optional/alternative). */
export function baseItemCost(item: any): number {
  return isBasePriced(item) ? calculateItemTotalCost(item) : 0;
}

/** Cost retained at full value when trade discounts are applied. */
export function tradeDiscountExcludedCost(items: any[]): number {
  return (items || []).reduce(
    (sum, item) => sum + (item?.exclude_from_trade_discount === true ? baseItemCost(item) : 0),
    0
  );
}

/**
 * Apply quote discounts while protecting item costs marked as non-discountable.
 * The distinction is internal only; quote documents continue to show one trade discount.
 */
export function applyQuoteDiscounts({
  subtotal,
  excludedCost,
  transportDiscount,
  tradeDiscountPercentage,
  tradeDiscountFixed,
}: {
  subtotal: number;
  excludedCost: number;
  transportDiscount: number;
  tradeDiscountPercentage: number;
  tradeDiscountFixed: number;
}) {
  const afterTransport = Math.max(subtotal - Math.max(transportDiscount, 0), 0);
  const protectedCost = Math.min(Math.max(excludedCost, 0), afterTransport);
  const discountableCost = Math.max(afterTransport - protectedCost, 0);
  const percentageAmount = discountableCost * (Math.max(tradeDiscountPercentage, 0) / 100);
  const afterPercentage = Math.max(discountableCost - percentageAmount, 0);
  const fixedAmount = Math.min(Math.max(tradeDiscountFixed, 0), afterPercentage);

  return {
    percentageAmount,
    fixedAmount,
    costAfterDiscounts: protectedCost + Math.max(afterPercentage - fixedAmount, 0),
  };
}

/**
 * Effective markup for an item: its own override when set, otherwise the
 * quote-level markup.
 */
export function effectiveMarkupPercent(item: any, quoteMarkupPct: number): number {
  const own = item?.markup_percentage;
  if (own !== null && own !== undefined && own !== "" && Number.isFinite(Number(own))) {
    return Number(own);
  }
  return Number(quoteMarkupPct) || 0;
}

/** True when any item carries its own markup override. */
export function hasMarkupOverrides(items: any[]): boolean {
  return (items || []).some(
    (i) => i?.markup_percentage !== null && i?.markup_percentage !== undefined && i?.markup_percentage !== ""
  );
}

/**
 * Cost-weighted blended markup across the items that count towards the base
 * total. Falls back to the quote markup when there is nothing to weigh.
 */
export function blendedMarkupPercent(items: any[], quoteMarkupPct: number): number {
  let cost = 0;
  let marked = 0;
  for (const it of items || []) {
    const c = baseItemCost(it);
    if (!c) continue;
    cost += c;
    marked += c * effectiveMarkupPercent(it, quoteMarkupPct);
  }
  if (cost <= 0) return Number(quoteMarkupPct) || 0;
  return marked / cost;
}

/**
 * Client-facing price of an item: raw cost with the applicable markup applied
 * (item override when set), so add-ons and swaps are priced on the same basis
 * as the base total.
 */
export function withClientPrices<T extends Record<string, any>>(items: T[], markupPct: number): T[] {
  return items.map((it) => {
    const factor = 1 + effectiveMarkupPercent(it, markupPct) / 100;
    return {
      ...it,
      client_price: Math.round(calculateItemTotalCost(it as any) * factor * 100) / 100,
    };
  });
}


/**
 * Line prices for the "marked-up items" presentation: each line carries its
 * own markup (item override, else the quote markup), then all lines are scaled
 * by one common factor so they sum exactly to `grossProgrammeCost` (which also
 * absorbs quote-level fees and the discounts shown as separate rows).
 */
export function markedUpLineTotals<T>(items: T[], quoteMarkupPct: number, grossProgrammeCost: number): Map<unknown, number> {
  const weights = (items || []).map(
    (it) => calculateItemTotalCost(it as any) * (1 + effectiveMarkupPercent(it, quoteMarkupPct) / 100)
  );
  const sum = weights.reduce((a, w) => a + w, 0);
  const scale = sum > 0 ? grossProgrammeCost / sum : 1;
  // Keyed by item id (falls back to the object) so regrouped copies still match.
  return new Map<unknown, number>((items || []).map((it, i) => [(it as any)?.id ?? it, weights[i] * scale]));
}

/**
 * For snapshots without the quote markup (client-branded copies), recover each
 * item's markup from its snapshotted client_price so repricing stays correct.
 */
export function withImpliedMarkup<T extends Record<string, any>>(items: T[]): T[] {
  return (items || []).map((it) => {
    const own = it?.markup_percentage;
    if (own !== null && own !== undefined && own !== "") return it;
    const cost = calculateItemTotalCost(it as any);
    const price = Number(it?.client_price);
    if (!(cost > 0) || !Number.isFinite(price) || it?.client_price == null) return it;
    return { ...it, markup_percentage: (price / cost - 1) * 100 };
  });
}

export function itemLabel(item: any): string {
  return item?.custom_title || item?.title || "Item";
}

export interface AlternativeGroup<T> {
  primary: T;
  alternatives: T[];
}

export function buildQuoteChoices<T extends ChoiceItem>(items: T[]) {
  const byId = new Map(items.map((i) => [i.id, i]));
  const optional = items.filter((i) => pricingTreatment(i) === "optional");
  const groups: AlternativeGroup<T>[] = [];
  const groupMap = new Map<string, AlternativeGroup<T>>();
  for (const it of items) {
    if (pricingTreatment(it) !== "alternative" || !it.alternative_to_item_id) continue;
    const primary = byId.get(it.alternative_to_item_id);
    if (!primary || !isBasePriced(primary)) continue;
    let g = groupMap.get(primary.id);
    if (!g) {
      g = { primary, alternatives: [] };
      groupMap.set(primary.id, g);
      groups.push(g);
    }
    g.alternatives.push(it);
  }
  return { optional, groups };
}

/**
 * A group "follows" another when any of its options is linked to an option of
 * that other group. Returns follower primary id -> leader primary id.
 */
export function linkedGroupLeaders<T extends ChoiceItem>(groups: AlternativeGroup<T>[]): Map<string, string> {
  const ownerOf = new Map<string, string>();
  for (const g of groups) for (const o of [g.primary, ...g.alternatives]) ownerOf.set(o.id, g.primary.id);
  const leaders = new Map<string, string>();
  for (const g of groups) {
    for (const o of [g.primary, ...g.alternatives]) {
      const leader = o.linked_option_item_id ? ownerOf.get(o.linked_option_item_id) : undefined;
      if (leader && leader !== g.primary.id) { leaders.set(g.primary.id, leader); break; }
    }
  }
  return leaders;
}

/**
 * Forces every linked choice to the option that goes with its leader's chosen
 * option (falling back to the follower's base option when none is linked).
 */
export function resolveLinkedSelections<T extends ChoiceItem>(items: T[], sel: QuoteClientSelections): QuoteClientSelections {
  const { groups } = buildQuoteChoices(items);
  const leaders = linkedGroupLeaders(groups);
  if (!leaders.size) return sel;
  const out: Record<string, string> = { ...(sel.alternative_selections || {}) };
  const byPrimary = new Map(groups.map((g) => [g.primary.id, g]));
  const chosenOf = (pid: string, depth = 0): string => {
    const lead = leaders.get(pid);
    const g = byPrimary.get(pid)!;
    if (!lead || depth > 5) return out[pid] || pid;
    const leaderChosen = chosenOf(lead, depth + 1);
    const match = [g.primary, ...g.alternatives].find((o) => o.linked_option_item_id === leaderChosen);
    return match ? match.id : g.primary.id;
  };
  for (const pid of leaders.keys()) out[pid] = chosenOf(pid);
  return { ...sel, alternative_selections: out };
}

export function priceOf(item: any): number {
  return Number(item?.client_price ?? calculateItemTotalCost(item)) || 0;
}

/**
 * Applies the recipient's selections: returns the items to display and the
 * amount to add to the base total.
 */
export function applyQuoteSelections<T extends ChoiceItem>(items: T[], sel: QuoteClientSelections) {
  const { groups } = buildQuoteChoices(items);
  sel = resolveLinkedSelections(items, sel);
  const chosenOptional = new Set(sel.optional_item_ids || []);
  const hidden = new Set<string>();
  let adjustment = 0;

  for (const g of groups) {
    const chosenId = sel.alternative_selections?.[g.primary.id] || g.primary.id;
    const chosen = [g.primary, ...g.alternatives].find((i) => i.id === chosenId) || g.primary;
    for (const i of [g.primary, ...g.alternatives]) if (i.id !== chosen.id) hidden.add(i.id);
    if (chosen.id !== g.primary.id) adjustment += priceOf(chosen) - priceOf(g.primary);
  }

  const visible = items.filter((i) => {
    if (hidden.has(i.id)) return false;
    const t = pricingTreatment(i);
    if (t === "optional") return chosenOptional.has(i.id);
    if (t === "alternative") {
      // orphaned alternatives (primary missing) are hidden
      return groups.some((g) => g.alternatives.some((a) => a.id === i.id));
    }
    return true;
  });

  for (const i of items) {
    if (pricingTreatment(i) === "optional" && chosenOptional.has(i.id)) adjustment += priceOf(i);
  }

  return { visible, adjustment };
}

export interface RepricedQuote {
  subtotal: number;
  transportDiscount: number;
  pctDiscountAmount: number;
  fixedDiscountAmount: number;
  additionalFees: any[];
  feesTotal: number;
  markupAmount: number;
  total: number;
}

/**
 * Re-run the full quote pricing pipeline over a set of visible items (after
 * client selections and any pax scaling). Flat discounts/fees stored on the
 * quote are scaled by paxFactor; percentage-based values are not.
 * This keeps option toggles consistent: the trade discount, fees and markup
 * are recalculated on the selected items instead of subtracting a gross
 * retail difference from an already-discounted total.
 */
export function repriceVisibleItems(visibleItems: any[], quote: any, paxFactor = 1): RepricedQuote {
  // applyQuoteSelections only returns items the client is actually getting:
  // chosen alternatives and switched-on optional extras. Normalize them to
  // "included" so their cost counts towards the repriced total.
  const items = (visibleItems || []).map((it) =>
    pricingTreatment(it) === "included" ? it : { ...it, pricing_treatment: "included" }
  );
  const subtotal = items.reduce((s, it) => s + baseItemCost(it), 0);
  const transportDiscount = Math.max(Number(quote?.transport_discount_amount || 0) * paxFactor, 0);
  const d = applyQuoteDiscounts({
    subtotal,
    excludedCost: tradeDiscountExcludedCost(items),
    transportDiscount,
    tradeDiscountPercentage: Number(quote?.discount_percentage || 0),
    tradeDiscountFixed: Math.max(Number(quote?.discount_fixed_amount || 0) * paxFactor, 0),
  });
  const costBeforeMarkup = d.costAfterDiscounts;
  const additionalFees = Array.isArray(quote?.additional_fees)
    ? quote.additional_fees
        .filter((f: any) => Number(f?.percentage) > 0)
        .map((f: any) => ({ ...f, amount: costBeforeMarkup * (Number(f.percentage) / 100) }))
    : [];
  const feesTotal = additionalFees.reduce((s: number, f: any) => s + f.amount, 0);
  const quoteMarkup = Number(quote?.markup_percentage || 0);
  const markupPct = hasMarkupOverrides(items) ? blendedMarkupPercent(items, quoteMarkup) : quoteMarkup;
  const markupAmount = (costBeforeMarkup + feesTotal) * (markupPct / 100);
  return {
    subtotal,
    transportDiscount,
    pctDiscountAmount: d.percentageAmount,
    fixedDiscountAmount: d.fixedAmount,
    additionalFees,
    feesTotal,
    markupAmount,
    total: costBeforeMarkup + feesTotal + markupAmount,
  };
}

/**
 * An issued quote's saved total is authoritative. Re-pricing the snapshot can
 * drift from it (e.g. the snapshot captured newer item settings than the total),
 * so repriced figures are scaled by stored ÷ repriced-base. With no client
 * changes the document then shows exactly the saved total; option swaps, extras
 * and pax scaling move it proportionally from there.
 */
export function issuedTotalAnchor(allItems: any[], quote: any): number {
  const stored = Number(quote?.total_amount) || 0;
  const base = repriceVisibleItems(applyQuoteSelections(allItems || [], EMPTY_SELECTIONS).visible, quote, 1).total;
  return stored > 0 && base > 0 ? stored / base : 1;
}

export function hasQuoteChoices(items: ChoiceItem[]): boolean {
  const { optional, groups } = buildQuoteChoices(items);
  return optional.length > 0 || groups.length > 0;
}

export interface HotelOptionScenario<T> {
  label: string;
  isBase: boolean;
  items: T[];
}

/**
 * For each alternative group, returns one item set per option with that option
 * swapped into the base total (primary becomes an excluded alternative).
 * Internal-only: used to preview margin per hotel option before generating a quote.
 */
export function hotelOptionScenarios<T extends ChoiceItem>(items: T[]) {
  const { groups } = buildQuoteChoices(items);
  return groups.map((g) => ({
    groupLabel: itemLabel(g.primary),
    options: [g.primary, ...g.alternatives].map((opt): HotelOptionScenario<T> => {
      const sel = resolveLinkedSelections(items, { ...EMPTY_SELECTIONS, alternative_selections: { [g.primary.id]: opt.id } });
      const swaps = new Map<string, string>();
      for (const gg of groups) {
        const c = sel.alternative_selections[gg.primary.id];
        if (c && c !== gg.primary.id) swaps.set(gg.primary.id, c);
      }
      const chosenIds = new Set(swaps.values());
      return {
        label: itemLabel(opt),
        isBase: opt.id === g.primary.id,
        items: swaps.size === 0
          ? items
          : items.map((i) =>
              chosenIds.has(i.id)
                ? ({ ...i, pricing_treatment: "included" } as T)
                : swaps.has(i.id)
                  ? ({ ...i, pricing_treatment: "alternative" } as T)
                  : i
            ),
      };
    }),
  }));
}

/** Cost and selling price of an item set using the quote's discounts, fees and markup. */
export function priceItemSet(
  items: any[],
  opts: {
    markupPct: number;
    transportDiscount: number;
    discountPct: number;
    discountFixed: number;
    feesTotal: (costBeforeMarkup: number) => number;
    extraCost?: number;
  }
) {
  const cost = items.reduce((s, it) => s + baseItemCost(it), 0) + (opts.extraCost || 0);
  const markupPct = hasMarkupOverrides(items) ? blendedMarkupPercent(items, opts.markupPct) : opts.markupPct;
  const d = applyQuoteDiscounts({
    subtotal: cost,
    excludedCost: tradeDiscountExcludedCost(items),
    transportDiscount: opts.transportDiscount,
    tradeDiscountPercentage: opts.discountPct,
    tradeDiscountFixed: opts.discountFixed,
  });
  const base = d.costAfterDiscounts + opts.feesTotal(d.costAfterDiscounts);
  const selling = base * (1 + markupPct / 100);
  return { cost, selling, margin: selling - cost, marginPct: selling > 0 ? ((selling - cost) / selling) * 100 : 0 };
}
